import { useState, useEffect, useRef, useCallback } from "react";
import Sidebar from "../../../../components/ui/sidebar";
import { Card, CardContent } from "../../../../components/ui/card";
import { Button } from "../../../../components/ui/button";
import { Input } from "../../../../components/ui/input";
import { useAuth } from "../../../../context/AuthContext";
import { Package, Plus, Filter, Search, X, Calendar, User, Building, MapPin } from "lucide-react";
import Swal from "sweetalert2";
import {
  obtenerEncomiendas,
  Encomienda,
} from "../../../../api/encomiendaService";
import { obtenerTorres, Torre } from "../../../../api/torreService";
import { obtenerApartamentos, Apartamento } from "../../../../api/apartamentoService";
import { obtenerEstatusEncomienda, EstatusEncomienda } from "../../../../api/estatusEncomiendaService";
import CreateEncomiendaModal from "./CreateEncomiendaModal";
import DetailEncomiendaModal from "./DetailEncomiendaModal";

export default function EncomiendaListPage() {
  const { session } = useAuth();
  
  const isLoadingCatalogosRef = useRef(false);
  const isLoadingEncomiendasRef = useRef(false);
  const catalogosLoadedRef = useRef(false);
  
  const [encomiendas, setEncomiendas] = useState<Encomienda[]>([]);
  const [filteredEncomiendas, setFilteredEncomiendas] = useState<Encomienda[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTorre, setSelectedTorre] = useState("");
  const [selectedApartamento, setSelectedApartamento] = useState("");
  const [selectedEstatus, setSelectedEstatus] = useState("");
  const [torres, setTorres] = useState<Torre[]>([]);
  const [apartamentos, setApartamentos] = useState<Apartamento[]>([]);
  const [estatusOptions, setEstatusOptions] = useState<EstatusEncomienda[]>([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEncomienda, setSelectedEncomienda] = useState<Encomienda | null>(null);

  const loadCatalogos = useCallback(async () => {
    if (isLoadingCatalogosRef.current) return;

    try {
      isLoadingCatalogosRef.current = true;
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) return;

      const torresResponse = await obtenerTorres({
        "unidad residencial codigo": unidadCodigo,
      });

      if (torresResponse.code === "1" && torresResponse.object_) {
        setTorres(torresResponse.object_);
      }

      const estatusResponse = await obtenerEstatusEncomienda();
      if (estatusResponse.code === "1" && estatusResponse.object_) {
        setEstatusOptions(estatusResponse.object_);
      }
    } catch (error) {
      console.error("Error al cargar catálogos:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Error al cargar los catálogos",
        confirmButtonColor: "#3d5a40",
      });
    } finally {
      isLoadingCatalogosRef.current = false;
    }
  }, [session?.unidad?.id]);

  const loadApartamentos = useCallback(async (torreCodigo: string) => {
    if (!torreCodigo || torreCodigo.trim() === "") {
      setApartamentos([]);
      return;
    }

    try {
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setApartamentos([]);
        return;
      }

      const response = await obtenerApartamentos({
        "unidad residencial codigo": unidadCodigo,
        "torre codigo": torreCodigo,
      });

      if (response.code === "1" && response.object_) {
        setApartamentos(response.object_);
      } else {
        setApartamentos([]);
      }
    } catch (error) {
      console.error("Error al cargar apartamentos:", error);
      setApartamentos([]);
    }
  }, [session?.unidad?.id]);

  const filterEncomiendas = useCallback(() => {
    if (!searchTerm.trim()) {
      setFilteredEncomiendas(encomiendas);
      return;
    }

    const term = searchTerm.toLowerCase();
    const filtered = encomiendas.filter(
      (enc) =>
        (enc.apt_nombre || "").toLowerCase().includes(term) ||
        (enc.tor_nombre || "").toLowerCase().includes(term) ||
        (enc.ten_nombre || "").toLowerCase().includes(term) ||
        (enc.enc_nombre_mensajero || "").toLowerCase().includes(term) ||
        (enc.een_nombre || "").toLowerCase().includes(term)
    );
    setFilteredEncomiendas(filtered);
  }, [searchTerm, encomiendas]);

  const loadEncomiendas = useCallback(async (isSilent: boolean = false) => {
    if (isLoadingEncomiendasRef.current) return;

    try {
      isLoadingEncomiendasRef.current = true;
      if (!isSilent) {
        setIsLoading(true);
      }
      
      const unidadCodigo = session?.unidad?.id || "";
      if (!unidadCodigo) {
        setEncomiendas([]);
        setFilteredEncomiendas([]);
        return;
      }

      const payload = {
        unidad_residencial_codigo: unidadCodigo,
        torre_codigo: selectedTorre || "",
        apartamento_codigo: selectedApartamento && selectedTorre ? selectedApartamento : "",
        estatus_codigo: selectedEstatus || "",
        encomienda_codigo: "",
      };

      const response = await obtenerEncomiendas(payload);

      if (response.code === "1" && response.object_) {
        const mappedEncomiendas = response.object_.map((apiData: any) => {
          const fechaRecibido = apiData.enc_fecha_recibido || "";
          const fechaRecibidoParts = fechaRecibido.split(" ");
          const fechaRecibidoDate = fechaRecibidoParts[0] || fechaRecibido;
          const horaRecibido = fechaRecibidoParts[1] || apiData.enc_hora_recibido || "";

          return {
            ...apiData,
            enc_fecha_recibido: fechaRecibidoDate,
            enc_hora_recibido: horaRecibido,
            enc_estatus: apiData.enc_estatus || apiData.een_codigo || "",
            tor_codigo: apiData.enc_torre || apiData.tor_codigo || "",
            tor_nombre: apiData.enc_torre_nombre || apiData.tor_nombre || "",
            apt_codigo: apiData.enc_apartamento || apiData.apt_codigo || "",
            apt_nombre: apiData.enc_apartamento_nombre || apiData.apt_nombre || "",
            ten_codigo: apiData.enc_tipo || apiData.ten_codigo || "",
            ten_nombre: apiData.enc_tipo_nombre || apiData.ten_nombre || "",
            een_codigo: apiData.enc_estatus || apiData.een_codigo || "",
            een_nombre: apiData.enc_estatus_nombre || apiData.een_nombre || "",
          };
        });

        const sorted = mappedEncomiendas.sort((a, b) => {
          const dateA = new Date(`${a.enc_fecha_recibido} ${a.enc_hora_recibido || ""}`);
          const dateB = new Date(`${b.enc_fecha_recibido} ${b.enc_hora_recibido || ""}`);
          return dateB.getTime() - dateA.getTime();
        });
        setEncomiendas(sorted);
        setFilteredEncomiendas(sorted);
      } else {
        setEncomiendas([]);
        setFilteredEncomiendas([]);
      }
    } catch (error) {
      console.error("Error al cargar encomiendas:", error);
      if (!isSilent) {
        const errorMessage = error instanceof Error ? error.message : "No se pudieron cargar las encomiendas";
        const mensajeTraducido = errorMessage.includes("Request failed") 
          ? "Error al cargar las encomiendas. Por favor, intente nuevamente."
          : errorMessage.includes("Network Error")
          ? "Error de conexión. Verifique su conexión a internet."
          : errorMessage;
        
        Swal.fire({
          icon: "error",
          title: "Error al cargar encomiendas",
          text: mensajeTraducido,
          confirmButtonColor: "#3d5a40",
          didOpen: (popup) => {
            (popup as HTMLElement).style.zIndex = '10001';
          },
        });
      }
    } finally {
      isLoadingEncomiendasRef.current = false;
      if (!isSilent) {
        setIsLoading(false);
      }
    }
  }, [session?.unidad?.id, selectedTorre, selectedApartamento, selectedEstatus]);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    if (unidadId && !catalogosLoadedRef.current && !isLoadingCatalogosRef.current) {
      catalogosLoadedRef.current = true;
      loadCatalogos();
    }
  }, [session?.unidad?.id, loadCatalogos]);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    if (unidadId) {
      loadEncomiendas();
    }
  }, [session?.unidad?.id, selectedTorre, selectedApartamento, selectedEstatus, loadEncomiendas]);

  useEffect(() => {
    const unidadId = session?.unidad?.id;
    if (!unidadId) return;

    const interval = setInterval(() => {
      if (!showCreateModal && !showDetailModal && !isLoadingEncomiendasRef.current) {
        loadEncomiendas(true);
      }
    }, 30000);

    return () => clearInterval(interval);
  }, [session?.unidad?.id, showCreateModal, showDetailModal, loadEncomiendas]);

  useEffect(() => {
    filterEncomiendas();
  }, [searchTerm, encomiendas, filterEncomiendas]);



  const handleTorreChange = (value: string) => {
    setSelectedTorre(value);
    setSelectedApartamento("");
    setApartamentos([]);
    
    if (value) {
      loadApartamentos(value);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setSelectedTorre("");
    setSelectedApartamento("");
    setSelectedEstatus("");
    setApartamentos([]);
  };

  const handleOpenDetail = (encomienda: Encomienda) => {
    setSelectedEncomienda(encomienda);
    setShowDetailModal(true);
  };

  const handleCloseDetail = (shouldReload: boolean) => {
    setShowDetailModal(false);
    setSelectedEncomienda(null);
    if (shouldReload) {
      setTimeout(() => {
        loadEncomiendas();
      }, 500);
    }
  };

  const handleCloseCreate = (created: boolean) => {
    setShowCreateModal(false);
    if (created) {
      setTimeout(() => {
        loadEncomiendas();
      }, 500);
    }
  };

  const getStatusColor = (status: string) => {
    const statusLower = (status || "").toLowerCase();
    if (statusLower.includes("entregad")) return "text-green-600 bg-green-50";
    if (statusLower.includes("portería") || statusLower.includes("porteria"))
      return "text-orange-600 bg-orange-50";
    return "text-gray-600 bg-gray-50";
  };

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300">
      <Sidebar />

      <main className="flex-1 p-4 sm:p-6 md:ml-64 transition-all">
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-military-600 flex items-center gap-2 sm:gap-3">
                <Package size={24} className="sm:w-8 sm:h-8" />
                Gestión de Encomiendas
              </h2>
              <p className="text-sm sm:text-base text-gray-600 mt-1">
                Administra las encomiendas recibidas en portería
              </p>
            </div>
            
            <Button
              onClick={() => setShowCreateModal(true)}
              className="bg-military-500 hover:bg-military-600 text-white rounded-lg flex items-center gap-2 shadow-lg w-full sm:w-auto"
            >
              <Plus size={20} />
              Nueva Encomienda
            </Button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-4 sm:mt-6">
            <Card className="border-l-4 border-military-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Total</p>
                    <p className="text-2xl font-bold text-gray-900">{encomiendas.length}</p>
                  </div>
                  <div className="bg-military-100 p-3 rounded-full">
                    <Package size={24} className="text-military-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-orange-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">En Portería</p>
                    <p className="text-2xl font-bold text-orange-600">
                      {encomiendas.filter((e) => (e.enc_estatus || e.een_codigo || "") === "PO").length}
                    </p>
                  </div>
                  <div className="bg-orange-100 p-3 rounded-full">
                    <Building size={24} className="text-orange-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-l-4 border-green-500">
              <CardContent className="pt-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Entregadas</p>
                    <p className="text-2xl font-bold text-green-600">
                      {encomiendas.filter((e) => (e.enc_estatus || e.een_codigo || "") === "EN").length}
                    </p>
                  </div>
                  <div className="bg-green-100 p-3 rounded-full">
                    <Package size={24} className="text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <Card className="mb-6 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                <Filter size={20} />
                Filtros
              </h3>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-600"
                >
                  <X size={16} className="mr-1" />
                  Limpiar
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-gray-600"
                >
                  {showFilters ? "Ocultar" : "Mostrar"}
                </Button>
              </div>
            </div>

            {showFilters && (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Search size={16} className="inline mr-1" />
                    Buscar
                  </label>
                  <Input
                    placeholder="Apartamento, tipo, mensajero..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Building size={16} className="inline mr-1" />
                    Torre
                  </label>
                  <select
                    value={selectedTorre}
                    onChange={(e) => handleTorreChange(e.target.value)}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                  >
                    <option value="">Todas las torres</option>
                    {torres.map((torre) => (
                      <option key={torre.tor_codigo} value={torre.tor_codigo}>
                        {torre.tor_nombre}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <MapPin size={16} className="inline mr-1" />
                    Apartamento
                  </label>
                  <select
                    value={selectedApartamento}
                    onChange={(e) => setSelectedApartamento(e.target.value)}
                    disabled={!selectedTorre}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  >
                    <option value="">Todos los apartamentos</option>
                    {apartamentos.map((apt) => (
                      <option key={apt.apt_codigo} value={apt.apt_codigo}>
                        {apt.apt_nombre}
                      </option>
                    ))}
                  </select>
                  {!selectedTorre && (
                    <p className="text-xs text-gray-500 mt-1">
                      Selecciona una torre primero
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <Package size={16} className="inline mr-1" />
                    Estado
                  </label>
                  <select
                    value={selectedEstatus}
                    onChange={(e) => setSelectedEstatus(e.target.value)}
                    className="w-full rounded-lg border-gray-300 focus:border-military-500 focus:ring-military-500 p-2"
                  >
                    <option value="">Todos los estados</option>
                    {estatusOptions.map((est, index) => (
                      <option key={est.een_codigo || index} value={est.een_codigo}>
                        {est.een_nombre}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="shadow-lg">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Encomiendas ({filteredEncomiendas.length})
            </h3>

            {isLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="h-12 w-12 animate-spin rounded-full border-4 border-t-military-500 border-gray-300 mb-4" />
                <p className="text-gray-600">Cargando encomiendas...</p>
              </div>
            ) : filteredEncomiendas.length === 0 ? (
              <div className="text-center py-12">
                <Package size={48} className="mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600">No se encontraron encomiendas</p>
                <p className="text-sm text-gray-500 mt-2">
                  {searchTerm || selectedTorre || selectedApartamento || selectedEstatus
                    ? "Intenta ajustar los filtros"
                    : "Agrega una nueva encomienda para comenzar"}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredEncomiendas.map((encomienda) => (
                  <div
                    key={encomienda.enc_codigo}
                    onClick={() => handleOpenDetail(encomienda)}
                    className="border rounded-lg p-4 hover:shadow-lg transition cursor-pointer bg-white"
                  >
                    <div className="relative mb-3 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center" style={{ height: "200px" }}>
                      {encomienda.enc_ruta_foto || encomienda.enc_foto_base64 ? (
                        <img
                          src={encomienda.enc_ruta_foto || encomienda.enc_foto_base64}
                          alt={encomienda.ten_nombre}
                          className="max-w-full max-h-full object-contain"
                          onError={(e) => {
                            const target = e.target as HTMLImageElement;
                            target.style.display = 'none';
                          }}
                        />
                      ) : null}
                      {(!encomienda.enc_ruta_foto && !encomienda.enc_foto_base64) && (
                        <div className="w-full h-full flex items-center justify-center">
                          <Package size={48} className="text-gray-400" />
                        </div>
                      )}
                      <div className="absolute top-2 right-2">
                        <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            encomienda.een_nombre
                          )}`}
                        >
                          {encomienda.een_nombre}
                        </span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold text-gray-900">
                          {encomienda.ten_nombre}
                        </span>
                        <span className="text-sm text-gray-600 font-mono">
                          {encomienda.apt_nombre}
                        </span>
                      </div>

                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <Building size={14} />
                          <span>Torre {encomienda.tor_nombre}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <User size={14} />
                          <span className="truncate">{encomienda.enc_nombre_mensajero || "N/A"}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Calendar size={14} />
                          <span>
                            {encomienda.enc_fecha_recibido} {encomienda.enc_hora_recibido}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>

      {showCreateModal && (
        <CreateEncomiendaModal
          isOpen={showCreateModal}
          onClose={handleCloseCreate}
        />
      )}

      {showDetailModal && selectedEncomienda && (
        <DetailEncomiendaModal
          isOpen={showDetailModal}
          encomienda={selectedEncomienda}
          onClose={handleCloseDetail}
        />
      )}
    </div>
  );
}

